import { api } from "../api";
import Button from "../components/buttons/Button";
import { brandConfig } from "../helpers/brandConfig";
import PasswordField from "../components/PasswordField";
import React, { useEffect, useState } from "react";
import Snackbar from "../components/snackbar/snackbar";
import { useNavigate, useSearchParams } from "react-router-dom";

type WelcomeState = "loading" | "set-password" | "already-set";

export default function Welcome() {
  const [welcomeState, setWelcomeState] = useState<WelcomeState>("loading");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const email = searchParams.get("email") || "";
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const response = await api.checkFirstTimePasswordStatus(email, token);
        if (response.ok) {
          const data = await response.json();
          setWelcomeState(
            data.firstPasswordSetAt ? "already-set" : "set-password"
          );
        } else {
          setWelcomeState("set-password");
        }
      } catch {
        setWelcomeState("set-password");
      }
    })();
  }, [email]);

  const sendNewPassword = async (e: any) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setErrorMessage("Passwords must match");
      return;
    }
    const response = await api.updatePassword(token, password);
    if (response.ok) {
      const responseData = await response.json();
      setSuccessMessage(responseData.message);
      setErrorMessage("");
      setPassword("");
      setConfirmPassword("");
      navigate("/login-form", {
        state: {
          successMessage: "Password set successfully! Please log in.",
          email,
        },
      });
    } else {
      const responseData = await response.json();
      setErrorMessage(responseData.message);
      setSuccessMessage("");
    }
  };

  return (
    <div className="bg-white shadow rounded-xl p-12 pb-4 w-3/4 md:w-1/2 lg:w-2/5 2xl:w-1/4 m-auto">
      <Snackbar
        message={successMessage}
        open={Boolean(successMessage)}
        onClose={() => setSuccessMessage("")}
      />
      <Snackbar
        message={errorMessage}
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage("")}
      />
      <a
        href={brandConfig.brandHomepageLink()}
        target="_blank"
        rel="noreferrer"
      >
        <img
          className="flex flex-col w-3/4 pb-6 m-auto cursor-pointer"
          src={brandConfig.navbarImage()}
          alt="brand logo"
        />
      </a>

      {welcomeState === "loading" && (
        <p className="text-center text-gray-500">Loading...</p>
      )}

      {welcomeState === "already-set" && (
        <div className="flex flex-col items-center gap-4">
          <h2 className="text-xl font-bold text-center">Welcome Back!</h2>
          <p className="text-center text-gray-600">
            You&apos;ve already set your password. Please log in to continue.
          </p>
          <Button
            additionalStyling="p-2 w-full"
            onClick={() => navigate("/login-form", { state: { email } })}
            style="primary"
            buttonId="go-to-login-button"
          >
            Go to Login
          </Button>
        </div>
      )}

      {welcomeState === "set-password" && (
        <>
          <h2 className="text-xl font-bold text-center pb-4">Welcome!</h2>
          <p className="text-center text-gray-600 pb-4">
            Set your password to get started.
          </p>
          <form className="flex flex-col" onSubmit={sendNewPassword}>
            <PasswordField
              handleInfoChange={(e: any) => setPassword(e.target.value)}
              placeholder="Create Password"
              value={password}
              data-testid="password-input"
            />
            <PasswordField
              handleInfoChange={(e: any) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password"
              value={confirmPassword}
              data-testid="password-confirmation-input"
            />
            <Button
              additionalStyling="p-2 mt-4"
              onClick={sendNewPassword}
              data-testid="set-password"
              style="primary"
              type="submit"
              buttonId="set-password-button"
            >
              Set Password
            </Button>
          </form>
        </>
      )}
    </div>
  );
}
